import React, {useEffect, useRef, useState} from 'react';
import { Template } from "../../../components/template/template.jsx";
import "../../../css/style-report.css";
import "../../../css/input-date-time.css";
import ReportUtils from "../../../utils/report-utils.js";
import axios from "axios";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import {Card} from "../../../components/card/card.jsx";
import Content from "./components/content.jsx";
import {subMonths} from "date-fns";
import NotAppliedContent from "../../../components/reports/report-menu/not-applied-content.jsx";
import HeaderButtons from "../../../components/reports/report-menu/header-buttons.jsx";
import NoDataContent from "../../../components/reports/report-menu/no-data-content.jsx";
import Menu from "../../../components/reports/report-menu/menu.jsx";
import Filters from "./components/filters.jsx";
import AutoDatetime from "../../../components/reports/auto-datetime/auto-datetime.jsx";
import SubScroll from "../../../components/sub-scroll/sub-scroll.jsx";
import {useQuery} from "@tanstack/react-query";
import bosService from "../../../services/web/bos-service.js";

const API_URL = import.meta.env.VITE_API_URL;

const initialFilters = () => ({
    dtStart: subMonths(new Date(), 1),
    dtEnd: new Date(),
    bo: '',
    relations: { label: "Todos", value: 'all' },
    status: [],
    userLogin: null,
    userName: null,
    userCpf: null,
});

const initialTableColumns = (boName) => ({
    bogcm_id: {label: boName, visible: true},
    time_action: {label: "Data/Hora", visible: true},
    validation_namewar: {label: "Agente", visible: true},
    status_value: {label: "Status", visible: true},
    rejected_justification: {label: "Motivo da reprovação", visible: true},
    status_observation: {label: "Observação", visible: true},
    timeline: {label: "Linha do tempo", visible: true, exportIgnore: true, printIgnore: true},
});

const BoAuditorship = () => {
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(null);

    const [collapsed, setCollapsed] = useState(false);
    const [fixedTop, setFixedTop] = useState(null);
    const [filters, setFilters] = useState(initialFilters);
    const buttonRef = useRef(null);

    const { data: boName } = useQuery({
        queryKey: ['boName'],
        queryFn: () => bosService.getBoName(),
        initialData: 'BO'
    });

    const filterLabels = {
        dtStart: "Data/hora inicial",
        dtEnd: "Data/hora final",
        bo: `Número ${boName}`,
        status: "Status",
        relations: "Relacionado à",
        userLogin: "Login",
        userName: "Nome",
        userCpf: "CPF"
    };

    const title = `Auditoria do ${boName}`;
    const [headerSelected, setHeaderSelected] = useState(-1);
    const [tableColumns, setTableColumns] = useState(() => initialTableColumns(boName));

    useEffect(() => {
        setTableColumns(previousColumns => ({
            ...previousColumns,
            bogcm_id: {...previousColumns.bogcm_id, label: boName},
        }));
    }, [boName]);

    useEffect(() => {
        if (!reportData) return;

        ReportUtils.appendFilters(
            ReportUtils.filters({
                ...filters,
                dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : "",
                dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : "",
                userLogin: filters?.userLogin?.label ? filters.userLogin.label : "",
                userName: filters?.userName?.label ? filters.userName.label: "",
                userCpf: filters?.userCpf?.label ? filters.userCpf.label : "",
                validator: null
            }, filterLabels)
        );

    }, [reportData]);

    const hasValue = (value) => { return String(value ?? "").trim() !== "" };

    const apply = async () => {
        ReportUtils.applyLoad({
            setLoading,
            clearContent: () => {
                setReportData(null)
            }
        });

        try {
            const periodValidation = ReportUtils.periodValidate(filters.dtStart, filters.dtEnd, undefined, undefined, !hasValue(filters.bo));

            if (!periodValidation.ok) {
                ReportUtils.alertErrorMessage(periodValidation.message);
                setLoading(false);
                return;
            }

            const selectedUserValue = filters.userLogin?.value ?? filters.userName?.value ?? filters.userCpf?.value ?? null;

            const formattedFilters = {
                dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : null,
                dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : null,
                bo: filters.bo ?? null,
                status: filters.status?.map(i => i.value) ?? [],
                relations: filters.relations?.value ?? 'all',
                validator: selectedUserValue ?? null,
            };

            const auditFilters = ReportUtils.filters({
                ...filters,
                dtStart: formattedFilters.dtStart,
                dtEnd: formattedFilters.dtEnd,
                userLogin: filters.userLogin?.label ?? null,
                userName: filters.userName?.label ?? null,
                userCpf: filters.userCpf?.label ?? null,
            }, filterLabels, true);

            const { data: response } = await axios.post(API_URL + "/bo_auditorship/apply", {
                ...formattedFilters,
                filters: auditFilters
            });

            const data = response.data?.BOsAuditorship;
            setReportData(data);

            const scale = window.visualViewport?.scale ?? 1;
            if (window.innerWidth <= 1440 || scale > 1) setCollapsed(true);

        } catch (error) {
            ReportUtils.alertErrorMessage("Erro ao gerar o relatório.");
            console.error("Erro ao gerar o relatório", error);
        } finally {
            setLoading(false);
        }
    }

    useGlobalKey("Enter", apply);

    const clearSearch = () => {
        setFilters(initialFilters);
        setReportData(null);
    }

    const toggleFilters = () => {
        if (!collapsed && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setFixedTop(rect.top);
        }

        setCollapsed(prev => !prev);
    }

    const printComponentRef = useRef(null);
    const totalData = reportData?.length ?? 0;

    return (
        <SubScroll dependency={[collapsed, reportData]}>
            <div className={"flex"}>

                <Template isLoading={loading}>
                    <Template.Header>
                        <div className="flex justify-between w-full">
                            <Template.Title>{title}</Template.Title>
                            <HeaderButtons
                                exportable={true}
                                exportInSheets={true}
                                printable={true}
                                headerChange={true}
                                visibilityColumns={true}
                                columns={tableColumns}
                                setColumns={setTableColumns}
                                printComponentRef={printComponentRef}
                                reportData={reportData}
                                data={totalData}
                                headerSelected={headerSelected}
                                setHeaderSelected={setHeaderSelected}
                                setCollapsed={setCollapsed}
                            />
                        </div>
                    </Template.Header>
                    <Template.Body className={`${collapsed ? 'p-[0]' : ''}`}>
                        <Menu buttonRef={buttonRef} toggleFilters={toggleFilters} fixedTop={fixedTop}
                              collapsed={collapsed} apply={apply} clearSearch={clearSearch}>

                            <Filters setFilters={setFilters} filters={filters} boName={boName} hasBo={hasValue(filters?.bo)}/>
                            <AutoDatetime setFilters={setFilters}/>

                        </Menu>
                    </Template.Body>
                </Template>

                <Card className={`w-full h-fit mr-2 ${!reportData || totalData === 0 ? 'mt-[59px]' : 'mt-[61px]' } print:!mt-[-15px] transform ${!reportData || totalData === 0 ? '!bg-[#f3f3f4] !border-none !-translate-y-[5px]' : ''} ${collapsed ? 'ml-2!' : ''}`}>
                    <Card.Body>
                        <div className={`flex-1 ${!reportData || totalData === 0 ? 'p-[0]' : 'px-[20px] py-[5px]'}`}>
                            <NotAppliedContent reportData={reportData}/>
                            <NoDataContent reportData={reportData} data={totalData}/>
                            <Content reportData={reportData} data={totalData} boName={boName} entity={headerSelected} title={title} collapsed={collapsed} setLoading={setLoading} printComponentRef={printComponentRef} tableColumns={tableColumns}/>
                        </div>
                    </Card.Body>
                </Card>

            </div>
        </SubScroll>
    );
};

export default BoAuditorship;